import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Terminal, Trash2, Database, Bug } from 'lucide-react';
import { db } from '../services/db';
// import { useAuth } from '../context/AuthContext';
import { FormResponse, FormMetadata } from '../../types';
import { globalLogger, LogEntry } from '../utils/logger';

export function DevConsole({
  forms
}: {
  forms?: FormMetadata[]
}) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const user = (() => { try { return JSON.parse(localStorage.getItem('asa_branca_user_cache') || 'null'); } catch(e) { return null; } })();
  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = globalLogger.subscribe((newLogs) => {
      setLogs(newLogs);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when logs change
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const clearLogs = () => globalLogger.clearLogs();

  const generateMockResponses = async (withGeo: boolean) => {
    if (!user || !forms || forms.length === 0) {
      console.error('Cannot generate mock data: user or forms not available');
      return;
    }

    setIsGenerating(true);
    try {

      let targetForm = forms?.[0];
      if (!targetForm) {
        try {
          const fetched = await db.getForms();
          if (fetched && fetched.length > 0) targetForm = fetched[0];
        } catch(e) {}
      }
      if (!targetForm) throw new Error("No forms available to attach mock response.");

      const now = new Date();

      const newResponse: Partial<FormResponse> = {
        tenantId: user.tenantId,
        formId: targetForm.id,
        formVersion: targetForm.currentVersion,
        respondentId: user.id,
        respondentName: user.name,
        startedAt: new Date(now.getTime() - 600000).toISOString(),
        submittedAt: now.toISOString(),
        status: 'submitted',
        data: {},
        deviceInfo: {
          userAgent: navigator.userAgent,
          ...(withGeo ? {
            geolocation: {
              lat: -23.5505 + (Math.random() * 0.1 - 0.05),
              lng: -46.6333 + (Math.random() * 0.1 - 0.05)
            }
          } : {})
        }
      };

      // Auto-fill some fields based on the latest version schema
      const latestVersion = targetForm.versions.find(v => v.version === targetForm.currentVersion);
      if (latestVersion && latestVersion.schema) {
        latestVersion.schema.forEach((field: any) => {
          if (field.type === 'number') {
            newResponse.data![field.id] = { value: Math.floor(Math.random() * 100) };
          } else if (field.type === 'text_short' || field.type === 'text') {
            newResponse.data![field.id] = { value: 'Mock Data ' + Math.random().toString(36).slice(2, 6) };
          } else if (field.type === 'yes_no') {
            newResponse.data![field.id] = { value: Math.random() > 0.5 ? 'true' : 'false' };
          }
        });
      }

      console.log(`[DevConsole] Submitting mock response... (Geo: ${withGeo})`);
      await db.submitResponse(newResponse as any, user);
      console.log('[DevConsole] Mock response submitted successfully!');

      // Force reload to fetch new data
      window.dispatchEvent(new Event('asa_branca_responses_changed'));

    } catch (e) {
      console.error('[DevConsole] Error generating mock response:', e);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-[9999] bg-gray-900 text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors opacity-70 hover:opacity-100 flex items-center justify-center"
        title="Abrir DevConsole"
      >
        <Terminal size={20} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 w-[450px] max-h-[500px] z-[9999] bg-gray-900 rounded-xl shadow-2xl flex flex-col border border-gray-700 overflow-hidden font-mono text-xs">
      <div className="bg-gray-950 p-3 border-b border-gray-700 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 text-green-400 font-bold">
          <Bug size={16} /> Dev Console
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearLogs} className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800 transition-colors" title="Limpar Logs">
            <Trash2 size={14} />
          </button>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800 transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>

      <div ref={logContainerRef} className="flex-1 overflow-y-auto p-3 space-y-1.5 custom-scrollbar bg-gray-900 min-h-[150px]">
        {logs.length === 0 ? (
          <div className="text-gray-600 text-center italic mt-10">Aguardando logs...</div>
        ) : (
          logs.map((log, i) => (
            <div key={i} className={`flex flex-col gap-1 break-all ${
              log.level === 'error' ? 'text-red-400 bg-red-950/30 p-1.5 rounded' :
              log.level === 'warn' ? 'text-yellow-400 bg-yellow-950/20 p-1.5 rounded' : 'text-gray-300'
            }`}>
              <div className="flex gap-2">
                <span className="text-gray-500 shrink-0">[{log.timestamp}]</span>
                <span className="font-semibold w-10 shrink-0 uppercase text-[10px] mt-0.5">{log.level}</span>
                <span className="flex-1 whitespace-pre-wrap font-sans text-[11px] leading-relaxed">{log.message}</span>
              </div>
              {log.source && (
                 <div className="pl-14 text-[10px] text-gray-500 italic flex items-center gap-1 opacity-70">
                    <span>↳ {log.source}</span>
                 </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="p-3 bg-gray-800 border-t border-gray-700 shrink-0 grid grid-cols-2 gap-2">
        <button
          onClick={() => generateMockResponses(false)}
          disabled={isGenerating}
          className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white p-2 rounded flex items-center justify-center gap-2 transition-colors font-sans text-xs font-semibold"
        >
          <Database size={14} /> Mock Genérico
        </button>
        <button
          onClick={() => generateMockResponses(true)}
          disabled={isGenerating}
          className="bg-blue-900/50 hover:bg-blue-800/60 border border-blue-700/50 disabled:opacity-50 text-blue-200 p-2 rounded flex items-center justify-center gap-2 transition-colors font-sans text-xs font-semibold"
        >
          <Database size={14} /> Mock com Geo
        </button>
      </div>
    </div>
  );
}
